import { useEffect, useMemo, useState, useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Snackbar,
  Typography,
} from "@mui/material";
import { createPortal } from "react-dom";
import { CustomInnerAccordion } from "../styles";
import { snakeToText } from "../utils";
import ChatbotAccordionTimelineItem from "./ChatbotAccordionTimelineItem";
import useGetWeddingTimeline from "../../../../../Hooks/useGetWeddingTimeline";
import usePatchWeddingTimeline from "../../../../../Hooks/usePatchWeddingTimeline";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import DndIcon from "../../../../../Assets/Icons/dnd-icon.svg";
import CircleIcon from "../../../../../Assets/Icons/circle-icon.svg";
import EditIcon from "../../../../../Assets/Icons/edit-icon.svg";
import CloseOutline from "../../../../../Assets/Icons/close-outline.svg";
import CircleCheckedIcon from "../../../../../Assets/Icons/cicle-checked.svg";
import ChatbotAccordionAddItem from "./ChatbotAccordionAddItem";

const ChatbotAccordionTimeline = ({
  chatId,
  aiTimeline,
  isParsing,
  disableAction,
  timelineSummaryData,
  isTimeline,
}) => {
  const { data, getData: getDataWeddingTimeline } = useGetWeddingTimeline({
    chatId,
  });
  const { mutate: patchWeddingTimeline } = usePatchWeddingTimeline();

  const [timeline, setTimeline] = useState({});
  const [expandedKey, setExpandedKey] = useState(new Set());
  const previousShownTimelineRef = useRef({});

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleMutateItem = ({ index, type, key, item }) => {
    return new Promise((resolve, reject) => {
      const newTimeline = JSON.parse(JSON.stringify(shownTimeline));

      let successMessage = "";

      if (type === "add") {
        const selectedTimeline = newTimeline[key];
        if (selectedTimeline) {
          selectedTimeline.push(item);
          successMessage = "Item added";
        }
      } else if (type === "delete") {
        const selectedTimeline = newTimeline[key];
        if (selectedTimeline) {
          selectedTimeline.splice(index, 1);
          successMessage = "Item removed";
        }
      } else {
        const selectedTimeline = newTimeline[key];
        if (selectedTimeline) {
          selectedTimeline[index] = item;
          successMessage = "Item updated";
        }
      }

      if (type !== "add") {
        setTimeline(newTimeline);
      }

      patchWeddingTimeline({ chatId, timeline: newTimeline })
        .then(() => {
          if (type === "add") {
            setTimeline(newTimeline);
          }
          setOpenSnackbar(true);
          setSnackbarMessage(successMessage);
          window.dispatchEvent(new Event("updateTimeline"));
          resolve();
        })
        .catch((err) => {
          setSnackbarMessage("Failed to update item");
          setOpenSnackbar(true);
          reject(err);
        });
    });
  };

  const handleDragEnd = ({ key, result }) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const newTimeline = JSON.parse(JSON.stringify(shownTimeline || {}));

    const sourceArray = newTimeline[key];

    const [removed] = sourceArray.splice(result.source.index, 1);
    sourceArray.splice(result.destination.index, 0, removed);

    setTimeline(newTimeline);

    patchWeddingTimeline({ chatId, timeline: newTimeline })
      .then(() => {
        setOpenSnackbar(true);
        setSnackbarMessage("Item updated");
        window.dispatchEvent(new Event("updateTimeline"));
      })
      .catch((err) => {
        setOpenSnackbar(true);
      });
  };

  const shownTimeline = useMemo(() => {
    if (Object.keys(timeline).length > 0) {
      return timeline;
    }

    if (Object.keys(aiTimeline).length > 0) {
      return aiTimeline;
    }

    return {};
  }, [aiTimeline, timeline]);

  useEffect(() => {
    setTimeline(data?.timeline || {});
  }, [data]);

  useEffect(() => {
    window.addEventListener("mutateSidePanel", () => {
      getDataWeddingTimeline();
    });
    return () =>
      window.removeEventListener("mutateSidePanel", () => {
        getDataWeddingTimeline();
      });
  }, [getDataWeddingTimeline]);

  useEffect(() => {
    const previousShownTimeline = previousShownTimelineRef.current;
    const currentKeys = Object.keys(shownTimeline);
    const previousKeys = Object.keys(previousShownTimeline);

    // Find new keys that weren't in the previous timeline
    const newKeys = currentKeys.filter((key) => !previousKeys.includes(key));

    // Only update expandedKey if there are new keys to add
    if (newKeys.length > 0) {
      setExpandedKey((prevExpandedKey) => {
        const newExpandedKey = new Set(prevExpandedKey);
        // Add only the new keys to the expanded set
        newKeys.forEach((key) => newExpandedKey.add(key));
        return newExpandedKey;
      });
    }

    // Update the ref with the current timeline for the next comparison
    previousShownTimelineRef.current = shownTimeline;
  }, [shownTimeline]);

  return (
    <>
      {createPortal(
        <Snackbar
          open={openSnackbar}
          message={snackbarMessage}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          onClose={() => setOpenSnackbar(false)}
          sx={{
            "& .MuiSnackbarContent-root": {
              minWidth: 50,
              fontFamily: "Inter, sans-serif",
              fontSize: "16px",
              backgroundColor: "#fff1f2",
              color: "#171717",
              borderRadius: "10px",
            },
          }}
        />,
        document.body
      )}
      {Object.entries(shownTimeline)?.map(([key, value]) => {
        const isWeddingDaySchedule = key === "wedding_day_schedule";
        const isArray = Array.isArray(value);
        return (
          <CustomInnerAccordion
            key={key}
            defaultExpanded
            expanded={expandedKey.has(key)}
            onChange={(_, isExpanded) => {
              const newExpandedKey = new Set(expandedKey);

              if (isExpanded) {
                newExpandedKey.add(key);
              } else {
                newExpandedKey.delete(key);
              }

              setExpandedKey(newExpandedKey);
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography
                  component="span"
                  sx={{ fontSize: "16px", fontWeight: 500, color: "#171717" }}
                >
                  {snakeToText(key)}
                </Typography>

                {!expandedKey.has(key) && isTimeline && timelineSummaryData ? (
                  <Typography fontSize="14px">
                    {timelineSummaryData[key]}
                  </Typography>
                ) : null}
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <DragDropContext
                onDragEnd={(result) => handleDragEnd({ key, result })}
              >
                <Droppable
                  droppableId="list"
                  renderClone={(provided, _snapshot, rubric) => {
                    const item = isWeddingDaySchedule
                      ? value?.[rubric.source.index]
                      : value?.[rubric.source.index];

                    return (
                      <div
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        ref={provided.innerRef}
                        className="chatbot-accordion__content-container"
                      >
                        <div className="chatbot-accordion__content">
                          <img src={DndIcon} alt="dnd-icon" />
                          <img
                            src={item?.done ? CircleCheckedIcon : CircleIcon}
                            alt="circle-icon"
                            style={{ cursor: "pointer" }}
                          />
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              rowGap: 4,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                color: "#643843",
                                fontWeight: 500,
                                textDecoration: item?.done
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {isWeddingDaySchedule ? item.event : item.task}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                color: "#171717",
                                fontWeight: 500,
                                textDecoration: item?.done
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {isWeddingDaySchedule
                                ? item.time
                                : item.relative_time}
                            </Typography>
                          </div>
                        </div>
                        <div className="chatbot-accordion__content-actions">
                          <img
                            src={EditIcon}
                            alt="edit-icon"
                            style={{ cursor: "pointer" }}
                          />
                          <img
                            src={CloseOutline}
                            alt="close-outline-icon"
                            style={{ cursor: "pointer", width: 20, height: 20 }}
                          />
                        </div>
                      </div>
                    );
                  }}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      {isArray && !isWeddingDaySchedule && (
                        <>
                          {value?.map((item, index) => {
                            const uniqueId = index + item.task + item.due_date;
                            return (
                              <ChatbotAccordionTimelineItem
                                key={uniqueId}
                                uniqueId={uniqueId}
                                index={index}
                                item={item}
                                isWeddingDaySchedule={isWeddingDaySchedule}
                                isParsing={isParsing}
                                disableAction={disableAction}
                                onMutateItem={({ type, item }) =>
                                  handleMutateItem({ index, type, key, item })
                                }
                              />
                            );
                          })}
                        </>
                      )}
                      {isArray && isWeddingDaySchedule && (
                        <>
                          {value?.map((item, index) => {
                            const uniqueId = index + item.event + item.time;
                            return (
                              <ChatbotAccordionTimelineItem
                                key={uniqueId}
                                uniqueId={uniqueId}
                                index={index}
                                item={item}
                                isWeddingDaySchedule={isWeddingDaySchedule}
                                isParsing={isParsing}
                                disableAction={disableAction}
                                onMutateItem={({ type, item }) =>
                                  handleMutateItem({ index, type, key, item })
                                }
                              />
                            );
                          })}
                        </>
                      )}
                      {provided?.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <ChatbotAccordionAddItem
                isParsing={isParsing}
                disableAction={disableAction}
                isWeddingDaySchedule={isWeddingDaySchedule}
                onAddItem={({ type, item }) =>
                  handleMutateItem({ key, type, item })
                }
              />
            </AccordionDetails>
          </CustomInnerAccordion>
        );
      })}
    </>
  );
};

export default ChatbotAccordionTimeline;
